/**
 * The property register: the rail narrows, the toolbar sorts/rates/views,
 * the main region shows cards, a table or the map for the same query.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useSearch } from '@tanstack/react-router'
import { IconChevronLeft, IconChevronRight } from '@tabler/icons-react'
import { useCards, usePatchPreferences, usePreferences, useSavedLists } from '../../api/queries'
import type { MinimumDealRating, SavedFilterPreset } from '../../api/types'
import { useExperience } from '../../experiences/useExperience'
import { useListMotion } from '../../theme/motion'
import { Shell, StatusKeys } from '../../app/Shell'
import { Alert, Button, Empty, PickerRow, Segmented, Skeletons, Switch } from '../../ui/kit'
import { FilterRail } from './FilterRail'
import { LotCard } from './LotCard'
import { ModelsPicker } from './ModelsPicker'
import { RegisterMap } from './RegisterMap'
import { SavedFilters } from './SavedFilters'
import { TableView } from './TableView'
import { DEAL_OPTIONS, DEFAULT_FILTERS, PAGE_SIZE, SORT_LABELS, readSession, toMarkerQuery, toQuery, writeSession, type Filters, type RegisterView } from './filters'
import { activeModelIds } from './models'
import { SAVED_FILTER_LIMIT, capturePreset, restorePreset } from './presets'

function useDebounced<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState(value)
  useEffect(() => { const t = window.setTimeout(() => setDebounced(value), delay); return () => window.clearTimeout(t) }, [value, delay])
  return debounced
}

const EMPTY_RING: [number, number][] = []

export function RegisterPage() {
  const search = useSearch({ from: '/' })
  const navigate = useNavigate({ from: '/' })
  const initial = useMemo(readSession, [])
  const [filters, setFilters] = useState<Filters>(() => ({ ...initial.filters, listId: search.listId ?? initial.filters.listId }))
  const [offset, setOffset] = useState(initial.offset)
  const [view, setView] = useState<RegisterView>(initial.view)
  const [presetId, setPresetId] = useState<string | null>(null)
  const [presetError, setPresetError] = useState<string | null>(null)
  const [mapSummary, setMapSummary] = useState({ located: 0, total: 0, unlocated: 0, truncated: false, pending: true, error: false })
  const experience = useExperience()
  const lists = useSavedLists()
  const preferences = usePreferences()
  const savePreferences = usePatchPreferences()
  const debouncedQ = useDebounced(filters.q, 250)
  const previousExperience = useRef(experience.active)

  useEffect(() => { writeSession(filters, offset, view) }, [filters, offset, view])
  useEffect(() => {
    if (search.listId !== undefined && search.listId !== filters.listId) { setFilters(f => ({ ...f, listId: search.listId ?? null })); setOffset(0) }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search.listId])
  useEffect(() => {
    if (previousExperience.current !== experience.active) { setOffset(0); setPresetId(null) }
    previousExperience.current = experience.active
  }, [experience.active])

  const update = useCallback((patch: Partial<Filters>) => {
    setFilters(f => ({ ...f, ...patch }))
    setOffset(0)
    setPresetId(null)
    if ('listId' in patch) void navigate({ to: '/', search: patch.listId ? { listId: patch.listId } : {} })
  }, [navigate])
  const reset = () => { setFilters(DEFAULT_FILTERS); setOffset(0); setPresetId(null); void navigate({ to: '/', search: {} }) }

  const spec = experience.spec
  const effective = useMemo(() => ({ ...filters, q: debouncedQ }), [filters, debouncedQ])
  const markerQuery = useMemo(() => toMarkerQuery(effective, spec), [effective, spec])
  const query = useMemo(() => toQuery(effective, spec, offset), [effective, spec, offset])
  const cards = useCards(query, experience.ready && view !== 'map')
  const [gridRef] = useListMotion<HTMLDivElement>()

  const modelIds = activeModelIds(preferences.data, spec)
  const models = cards.data?.selectedDealModels ?? []
  const total = view === 'map' ? mapSummary.total : cards.data?.page.total
  const page = Math.floor(offset / PAGE_SIZE) + 1
  const scopeName = filters.listId ? (lists.data?.find(l => l.id === filters.listId)?.name ?? 'Selected list') : 'All properties'
  const sortOptions = spec.register.sorts.map(value => ({ value, label: SORT_LABELS[value] }))
  const presets = (preferences.data?.savedFilters ?? []).filter(p => p.experience === spec.presetKey)

  const savePreset = async (name: string) => {
    if (!preferences.data) throw new Error('Personal preferences are not ready.')
    if (preferences.data.savedFilters.length >= SAVED_FILTER_LIMIT) throw new Error('You can save up to 50 filters. Delete one to save another.')
    const preset = capturePreset(name, spec, filters, modelIds ?? [])
    await savePreferences.mutateAsync({ savedFilters: [...preferences.data.savedFilters, preset], expectedSavedFiltersRevision: preferences.data.savedFiltersRevision })
    setPresetId(preset.id); setPresetError(null)
  }
  const loadPreset = async (preset: SavedFilterPreset) => {
    try {
      const restored = restorePreset(preset, spec)
      if (spec.models.kind === 'userPick' && restored.dealModelIds.length) await savePreferences.mutateAsync({ dealModelIds: restored.dealModelIds })
      setFilters(restored.filters); setOffset(0); setPresetId(preset.id); setPresetError(null)
      void navigate({ to: '/', search: restored.filters.listId ? { listId: restored.filters.listId } : {} })
    } catch (failure) { setPresetError(failure instanceof Error ? failure.message : 'The filter could not be loaded.') }
  }
  const deletePreset = (id: string) => {
    if (!preferences.data) return
    savePreferences.mutate({ savedFilters: preferences.data.savedFilters.filter(p => p.id !== id), expectedSavedFiltersRevision: preferences.data.savedFiltersRevision }, {
      onSuccess: () => { setPresetId(null); setPresetError(null) }, onError: f => setPresetError(f.message),
    })
  }

  const countLabel = total === undefined ? '…' : `${total.toLocaleString('en-GB')} ${total === 1 ? 'property' : 'properties'}`
  const [countPulse, setCountPulse] = useState(0)
  const lastTotal = useRef<number | undefined>(undefined)
  useEffect(() => { if (total !== undefined && lastTotal.current !== undefined && lastTotal.current !== total) setCountPulse(n => n + 1); lastTotal.current = total }, [total])

  return (
    <Shell
      railLabel="Property register filters"
      fill={view === 'map'}
      rail={<FilterRail filters={filters} spec={spec} markerQuery={markerQuery} onChange={update} onReset={reset}
        savedFilters={<SavedFilters presets={presets} selectedId={presetId} saving={savePreferences.isPending} ready={preferences.isSuccess && experience.ready} error={presetError ?? preferences.error?.message ?? null} onSave={savePreset} onLoad={p => { void loadPreset(p) }} onDelete={deletePreset} />} />}
      head={
        <div className="toolbar">
          {filters.listId ? <div className="tt"><span className="scope">{scopeName}</span></div> : null}
          <div className="ta">
            <PickerRow variant="tool" label="Sort" options={sortOptions} value={filters.sort} defaultValue="updated_desc" clearable={false} onChange={v => update({ sort: (v as Filters['sort']) ?? 'updated_desc' })} />
            {spec.register.filters.includes('visited') ? <Switch className="tool-switch" label="Hide visited" checked={filters.visited === false} onChange={on => update({ visited: on ? false : null })} /> : null}
            {spec.models.kind === 'userPick' ? (
              preferences.isError || (preferences.data && modelIds === null)
                ? <span className="tool-field" title={preferences.error?.message ?? 'The saved model selection is invalid.'}><span>Models</span><b className="t-faint">unavailable</b></span>
                : <ModelsPicker selectedIds={modelIds ?? []} max={spec.models.max} saving={savePreferences.isPending && savePreferences.variables?.dealModelIds !== undefined} error={savePreferences.variables?.dealModelIds !== undefined ? savePreferences.error?.message ?? null : null} onChange={ids => savePreferences.mutate({ dealModelIds: ids }, { onSuccess: () => setOffset(0) })} />
            ) : null}
            {spec.register.minimumDeal ? (
              <div className="tool-group"><span>Minimum deal</span><Segmented<'all' | MinimumDealRating> label="Minimum deal" value={filters.minDealRating ?? 'all'} onChange={v => update({ minDealRating: v === 'all' ? null : v })} options={DEAL_OPTIONS} /></div>
            ) : null}
          </div>
          <div className="ta is-end">
            <Segmented<RegisterView> label="View" value={view} onChange={setView} options={[{ value: 'cards', label: 'Cards' }, { value: 'table', label: 'Table' }, { value: 'map', label: 'Map' }]} />
            <span className={countPulse ? 'pill is-changed' : 'pill'} key={countPulse} role="status"><span className="dot" />{countLabel}</span>
          </div>
        </div>
      }
      status={<>
        <span>{view === 'map' ? (mapSummary.pending ? 'Loading pins' : `${mapSummary.located.toLocaleString('en-GB')} pins · ${mapSummary.unlocated.toLocaleString('en-GB')} without coordinates${mapSummary.truncated ? ' · limited to 5,000' : ''}`) : total === undefined ? 'Loading' : `${total.toLocaleString('en-GB')} lots`}{view !== 'map' && cards.data ? ` · page ${page}` : ''}{cards.isFetching ? ' · updating' : ''}</span>
        <span>{spec.label}</span>
        <span className="sp" />
        <StatusKeys items={[['↵', 'open']]} />
      </>}
    >
      {view === 'map' ? (
        <div className="map-view view-enter" key="map">
          <RegisterMap markerQuery={markerQuery} ring={filters.locationArea?.ring ?? EMPTY_RING} spec={spec} listId={filters.listId ?? undefined} enabled={experience.ready} onSummary={setMapSummary} fitTo="markers">
            <div className="map-legend" aria-label="Pin colours"><span><i className="is-auction" />Auction</span><span><i className="is-private" />Private treaty</span><span><i className="is-unscreened" />Other</span></div>
            <div className="map-summary">{mapSummary.error ? <span className="chip">Pins unavailable</span> : null}</div>
          </RegisterMap>
        </div>
      ) : cards.isError ? (
        <Alert title="The register is unavailable">{cards.error.message}</Alert>
      ) : cards.isPending ? (
        view === 'table' ? <div className="tbl-wrap"><Skeletons count={10} kind="row" /></div> : <div className="grid" aria-busy="true"><Skeletons count={8} /></div>
      ) : cards.data.items.length === 0 ? (
        <Empty title="No properties match">{filters.listId && filters.status !== 'all' ? `${scopeName} holds ${lists.data?.find(l => l.id === filters.listId)?.propertyCount ?? 'some'} properties, none with status ${filters.status}. Set Status to All to see them.` : spec.register.minimumDeal && filters.minDealRating ? 'No saved rating from your selected models meets this minimum alongside the other filters. Lower the minimum or choose All to include unrated properties.' : 'Adjust the filters to broaden the result set.'}</Empty>
      ) : (
        <>
          {view === 'table' ? (
            <div className="view-enter" key="table"><TableView items={cards.data.items} models={models} spec={spec} listId={filters.listId ?? undefined} sort={filters.sort} onSort={sort => update({ sort })} /></div>
          ) : (
            <div className="grid view-enter stagger" key={`cards:${offset}:${query.sort}`} ref={gridRef} aria-busy={cards.isFetching || undefined}>
              {cards.data.items.map((item, i) => <div key={item.propertyId} style={{ '--i': i } as React.CSSProperties}><LotCard item={item} models={models} spec={spec} listId={filters.listId ?? undefined} /></div>)}
            </div>
          )}
          <div className="grid-foot">
            <Button small disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}><IconChevronLeft className="ic-sm" />Previous</Button>
            <span>Page {page}</span>
            <Button small disabled={!cards.data.page.hasMore} onClick={() => setOffset(cards.data.page.nextOffset ?? offset + PAGE_SIZE)}>Next<IconChevronRight className="ic-sm" /></Button>
          </div>
        </>
      )}
    </Shell>
  )
}
